'use client';

import { m, LazyMotion, domAnimation, useInView } from 'framer-motion';
import { useRef } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Grid, useColorScheme } from '@mui/material';

import { CONFIG } from 'src/global-config';
import { Iconify } from 'src/components/iconify';
import { useLocale } from 'src/locales';
import { t } from 'src/utils/helper';

// ----------------------------------------------------------------------

const SERVICES = [
  {
    title: 'Unified_Enterprise_Platform',
    description: 'Unified_Enterprise_Platform_desc',
    icon: 'streamline-cyber:multi-platform-2',
  },
  {
    title: 'Proven_Reliability',
    description: 'Proven_Reliability_desc',
    icon: 'mdi:approve',
  },
  {
    title: 'Full_Transparency',
    description: 'Full_Transparency_desc',
    icon: 'streamline-plump:transparent',
  },
  {
    title: 'Integrated_Technical_Onsite_Support',
    description: 'Integrated_Technical_Onsite_Support_desc',
    icon: 'material-symbols-light:support',
  },
];

// ----------------------------------------------------------------------

export function OurSolutionsInclude({ sx, ...other }) {
  const { mode } = useColorScheme();
  const { t } = useLocale();

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <LazyMotion features={domAnimation}>
      <Box
        ref={ref}
        component="section"
        sx={[
          {
            textAlign: 'center',
            pt: { xs: 5, md: 5 },
            pb: { xs: 5, md: 5 },
            bgcolor: mode === 'light' ? 'white' : 'background.neutral',
            overflow: 'hidden',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <Container>
          {/* Title */}
          <m.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h2" component="h2" sx={{ fontSize: '2rem !important' }}>
              {t('Trusted_Partner_for_Operational_Excellence')}
            </Typography>
            <Typography sx={{ mt: 3, color: 'text.secondary' }}>
              {t('Trusted_Partner_for_Operational_Excellence_Desc')}
            </Typography>
          </m.div>

          {/* Content Grid */}
          <Grid container spacing={3} sx={{ mt: 5, mb: 3 }} alignItems="center">
            {/* Image */}
            <Grid size={{ xs: 12, md: 4 }}>
              <m.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  component="img"
                  src={`${CONFIG.assetsDir}/assets/images/home/OurSolutions/saudi-arabia-trailer-truck-rental.webp`}
                  alt={t(
                    'Semi-trailer-truck-rental-for-full-truckload-cargo-transport-between-KSA-cities'
                  )}
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 480,
                    borderRadius: 2,
                    maxWidth: {  sm: 400, md: 480 },
                    mx: 'auto',
                    maxHeight: { xs: 200, sm: 300, md: 480 },
                    objectFit: 'cover',
                  }}
                />
              </m.div>
            </Grid>

            {/* Services */}
            <Grid
              size={{ xs: 12, md: 8 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: { xs: 1, md: 7 },
              }}
            >
              {[SERVICES.slice(0, 2), SERVICES.slice(2)].map((group, gIndex) => (
                <Grid
                  key={gIndex}
                  spacing={3}
                  sx={{
                    flex: 1,
                    alignContent: 'stretch',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    display: 'grid',
                    flexGrow: 1,
                    textAlign: 'justify',
                  }}
                >
                  {group.map((service, i) => (
                    <m.div
                      key={service.title}
                      initial={{ opacity: 0, y: 40 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.3 + (gIndex * 2 + i) * 0.15 }}
                    >
                      <Box>
                        <Iconify
                          icon={service.icon}
                          width={45}
                          height={45}
                          sx={{ color: 'primary.main', mx: 2 }}
                        />
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1">{t(service.title)}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {t(service.description)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </m.div>
                  ))}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Box>
    </LazyMotion>
  );
}
